import { useState } from "react"
import { QRCodeSVG } from "qrcode.react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query"
import { api, rawFetch } from "@/lib/api"
import { passkeysSupported, registerPasskey } from "@/lib/webauthn"
import { auth } from "@/lib/auth"
import { timeAgo } from "@/lib/utils"
import { useT } from "@/lib/i18n"
import { Badge, Button, Card, CardBody, CardHeader, Input, Sheet, Spinner, Toast } from "@/components/ui"
import type { SessionOut, TotpEnrollOut, TotpActivateOut } from "@/lib/types"
import { CheckIcon, CopyIcon, DownloadIcon, KeySquareIcon, LogOutIcon, MailIcon, Share2Icon, ShieldOffIcon, Trash2Icon } from "lucide-react"

export const Route = createFileRoute("/_app/account")({
  component: AccountPage,
})

function AccountPage() {
  const me = auth.me()
  const nav = useNavigate()
  const qc = useQueryClient()
  const [emailSheet, setEmailSheet] = useState(false)
  const [twoFASheet, setTwoFASheet] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  async function exportData() {
    setExporting(true); setError(null)
    try {
      const r = await rawFetch("/api/me/export")
      if (!r.ok) throw new Error(`Export failed (${r.status})`)
      const blob = await r.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `lucy-export-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setExporting(false)
    }
  }

  const revokeAll = useMutation({
    mutationFn: () => api("/api/auth/sessions/revoke-all", { method: "POST" }),
    onSuccess: async () => {
      setInfo("All other sessions signed out.")
      await auth.refreshMe()
    },
  })

  async function deleteAccount() {
    if (!window.confirm("Delete your account? You have 7 days to change your mind before data is purged.")) return
    if (!window.confirm("Really delete? This cannot be undone after the grace period.")) return
    try {
      await api("/api/me", { method: "DELETE" })
      await auth.logout()
      await nav({ to: "/login" })
    } catch (e) {
      setError((e as Error).message)
    }
  }


  async function saveProfile(body: { display_name?: string; tz?: string }) {
    try {
      await api("/api/me/profile", { method: "PATCH", body })
      await auth.refreshMe()
      setError(null)
    } catch (e) {
      setError((e as { detail?: string }).detail || "Could not save.")
      throw e
    }
  }
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Account</h1>
        <p className="text-[13px] text-muted-foreground mt-1.5">
          Profile, security, and the controls you should never have to think about.
        </p>
      </div>

      {info && <Toast variant="ok">{info}</Toast>}
      {error && <Toast variant="destructive">{error}</Toast>}

      <Card>
        <CardHeader title="Profile" />
        <CardBody className="space-y-3 text-sm">
          <Row label="Email">
            <div className="flex items-center gap-2">
              <span className="font-mono">{me?.email}</span>
              {(me?.email_verified_at || me?.email_verified) ? <Badge variant="ok">verified</Badge> : <Badge variant="warn">unverified</Badge>}
              <Button variant="outline" size="sm" onClick={() => setEmailSheet(true)}>
                <MailIcon className="h-3 w-3" /> Change
              </Button>
            </div>
          </Row>
          <Row label="Display name">
            <InlineEdit value={me?.display_name ?? ""} placeholder="—" onSave={(v) => saveProfile({ display_name: v })} />
          </Row>
          <Row label="Timezone">
            <InlineEdit value={me?.tz ?? "UTC"} mono onSave={(v) => saveProfile({ tz: v })} />
          </Row>
          <Row label="Tier">
            <Badge variant={me?.tier === "free" ? "muted" : "ok"}>{me?.tier}</Badge>
          </Row>
          <Row label="Member since">
            <span className="font-mono text-xs">{me?.created_at?.slice(0, 10)}</span>
          </Row>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Security" subtitle="2FA and sessions" />
        <CardBody className="space-y-3 text-sm">
          <Row label="Two-factor auth">
            <div className="flex items-center gap-2">
              {me?.totp_enabled ? <Badge variant="ok">enabled</Badge> : <Badge variant="muted">disabled</Badge>}
              <Button variant="outline" size="sm" onClick={() => setTwoFASheet(true)}>
                <KeySquareIcon className="h-3 w-3" /> {me?.totp_enabled ? "Manage" : "Enable"}
              </Button>
            </div>
          </Row>
          <Row label="Sessions">
            <Button variant="outline" size="sm" onClick={() => {
              if (window.confirm("Sign out of every other browser/device?")) revokeAll.mutate()
            }}>
              <LogOutIcon className="h-3 w-3" /> Sign out everywhere
            </Button>
          </Row>
          <SessionsList />
        </CardBody>
      </Card>

      <PasskeysCard />

      <ReferralCard />

      <Card>
        <CardHeader title="Your data" subtitle="GDPR-compliant export & deletion" />
        <CardBody className="space-y-3 text-sm">
          <Row label="Download a copy">
            <Button variant="outline" size="sm" onClick={exportData} disabled={exporting}>
              {exporting ? <Spinner /> : <DownloadIcon className="h-3 w-3" />} Export JSON
            </Button>
          </Row>
          <Row label="Delete account">
            <Button variant="destructive" size="sm" onClick={deleteAccount}>
              <Trash2Icon className="h-3 w-3" /> Delete
            </Button>
          </Row>
          <p className="text-[10px] text-muted-foreground">
            Soft-deleted for 7 days. After that, all PII is purged. Audit logs retained for compliance.
          </p>
        </CardBody>
      </Card>

      <ChangeEmailSheet open={emailSheet} onClose={() => setEmailSheet(false)} />
      <TwoFASheet open={twoFASheet} onClose={() => { setTwoFASheet(false); qc.invalidateQueries() }} />
    </div>
  )
}

/** Passkeys: Face ID / Touch ID / a security key instead of the password. */
function PasskeysCard() {
  const qc = useQueryClient()
  const [name, setName] = useState("")
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const list = useQuery({ queryKey: ["passkeys"], queryFn: () => api<PasskeyOut[]>("/api/auth/passkeys") })
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/auth/passkeys/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["passkeys"] }),
  })
  async function add() {
    setBusy(true); setErr(null)
    try {
      await registerPasskey(name.trim() || undefined)
      setName("")
      await qc.invalidateQueries({ queryKey: ["passkeys"] })
    } catch (e) {
      const msg = (e as { detail?: string }).detail || (e as Error).message
      setErr(msg === "cancelled" ? null : msg)
    } finally { setBusy(false) }
  }
  const supported = passkeysSupported()
  return (
    <Card>
      <CardHeader title="Passkeys" subtitle="Sign in with Face ID, Touch ID or a security key — no password to type or phish." />
      <CardBody className="space-y-3 text-sm">
        {!supported && <p className="text-xs text-muted-foreground">This browser doesn't support passkeys.</p>}
        {(list.data ?? []).length === 0 && list.isSuccess && <p className="text-xs text-muted-foreground">No passkeys yet.</p>}
        <ul className="divide-y divide-border">
          {(list.data ?? []).map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <div className="truncate font-medium">{p.name || "Passkey"}{p.backed_up && <Badge variant="muted" className="ml-2">synced</Badge>}</div>
                <div className="font-mono text-[11px] text-muted-foreground">
                  added {new Date(p.created_at).toLocaleDateString()}{p.last_used_at ? ` · used ${new Date(p.last_used_at).toLocaleDateString()}` : ""}
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => { if (window.confirm("Remove this passkey?")) remove.mutate(p.id) }}>Remove</Button>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap items-center gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (e.g. MacBook, iPhone)" className="w-56" />
          <Button size="sm" onClick={add} disabled={busy || !supported}>{busy ? <Spinner /> : <KeySquareIcon className="h-3 w-3" />} Add passkey</Button>
        </div>
        {err && <Toast variant="destructive">{err}</Toast>}
      </CardBody>
    </Card>
  )
}

interface PasskeyOut { id: string; name: string | null; created_at: string; last_used_at: string | null; backed_up: boolean; transports: string[] | null }

interface ReferralOut {
  code: string
  url: string
  enabled: boolean
  credit_usd: number
  valid_days: number
  referred_count: number
  credits_earned_usd: number
  credits_available_usd: number
  referred: { name: string; created_at: string; verified: boolean }[]
}

/** Invite friends: personal link, what both sides get, who came through it. */
function ReferralCard() {
  const t = useT()
  const [copied, setCopied] = useState(false)
  const q = useQuery({ queryKey: ["me/referral"], queryFn: () => api<ReferralOut>("/api/me/referral") })
  const d = q.data
  // Build from this origin so the link always points at the app the user is on.
  const link = d ? `${window.location.origin}/signup?ref=${encodeURIComponent(d.code)}` : ""
  const amount = d ? d.credit_usd.toFixed(0) : "0"
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function"

  async function copy() {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { /* clipboard blocked — the field is selectable */ }
  }
  async function share() {
    try {
      await navigator.share({ title: "Lucy", text: t("ref.shareText", { amount }), url: link })
    } catch { /* dismissed */ }
  }

  return (
    <Card>
      <CardHeader title={t("ref.title")} subtitle={d ? t("ref.sub", { amount, days: d.valid_days }) : ""} />
      <CardBody className="space-y-3 text-sm">
        {q.isLoading && <Spinner />}
        {q.isError && <p className="text-xs text-destructive">{t("ref.loadFailed")}</p>}
        {d && !d.enabled && <p className="text-xs text-muted-foreground">{t("ref.disabled")}</p>}
        {d && d.enabled && (
          <>
            <div className="space-y-1.5">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("ref.link")}</div>
              <div className="flex flex-wrap items-center gap-2">
                <Input readOnly value={link} onFocus={(e) => e.currentTarget.select()} className="min-w-0 flex-1 font-mono text-xs" data-testid="referral-link" />
                <Button variant="outline" size="sm" onClick={copy}>
                  {copied ? <CheckIcon className="h-3 w-3" /> : <CopyIcon className="h-3 w-3" />} {copied ? t("ref.copied") : t("ref.copy")}
                </Button>
                {canShare && (
                  <Button variant="outline" size="sm" onClick={share}>
                    <Share2Icon className="h-3 w-3" /> {t("ref.share")}
                  </Button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <Stat label={t("ref.referredCount")} value={String(d.referred_count)} />
              <Stat label={t("ref.earned")} value={`$${d.credits_earned_usd.toFixed(2)}`} />
              <Stat label={t("ref.available")} value={`$${d.credits_available_usd.toFixed(2)}`} />
            </div>
            {d.referred.length === 0 ? (
              <p className="text-xs text-muted-foreground">{t("ref.none")}</p>
            ) : (
              <ul className="divide-y divide-border/60 text-xs">
                {d.referred.map((r, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 py-1.5">
                    <span className="truncate font-medium">{r.name}</span>
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <span className="font-mono">{r.created_at.slice(0, 10)}</span>
                      <Badge variant={r.verified ? "ok" : "muted"}>{r.verified ? t("ref.verified") : t("ref.pending")}</Badge>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </CardBody>
    </Card>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border px-2 py-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-semibold tabular-nums">{value}</div>
    </div>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-border/50 py-2 last:border-0">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      {children}
    </div>
  )
}

function ChangeEmailSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [email, setEmail] = useState("")
  const [pw, setPw] = useState("")
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  async function go() {
    setBusy(true); setErr(null)
    try {
      await api("/api/me/email", { method: "POST", body: { new_email: email, password: pw } })
      setDone(true)
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Change email"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          {!done && <Button onClick={go} disabled={busy || !email || !pw}>{busy ? <Spinner /> : null}Send link</Button>}
        </>
      }
    >
      {done ? (
        <Toast variant="ok">Verification sent to <span className="font-mono">{email}</span>. Click the link to swap.</Toast>
      ) : (
        <div className="space-y-3 text-sm">
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wider text-muted-foreground">New email</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Current password</label>
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} />
          </div>
          {err && <Toast variant="destructive">{err}</Toast>}
        </div>
      )}
    </Sheet>
  )
}

function TwoFASheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const me = auth.me()
  const [phase, setPhase] = useState<"idle" | "enrolling" | "show-recovery" | "disabling">("idle")
  const [enroll, setEnroll] = useState<TotpEnrollOut | null>(null)
  const [code, setCode] = useState("")
  const [password, setPassword] = useState("")
  const [recovery, setRecovery] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function startEnroll() {
    setBusy(true); setErr(null)
    try {
      const r = await api<TotpEnrollOut>("/api/auth/2fa/enroll", { method: "POST" })
      setEnroll(r)
      setPhase("enrolling")
    } catch (e) { setErr((e as Error).message) } finally { setBusy(false) }
  }

  async function activate() {
    setBusy(true); setErr(null)
    try {
      const r = await api<TotpActivateOut>("/api/auth/2fa/verify", { method: "POST", body: { totp: code.replace(/\s+/g, "") } })
      setRecovery(r.recovery_codes)
      setPhase("show-recovery")
      await auth.refreshMe()
    } catch (e) { setErr((e as Error).message) } finally { setBusy(false) }
  }

  async function disable() {
    setBusy(true); setErr(null)
    try {
      await api("/api/auth/2fa/disable", {
        method: "POST",
        body: { password, totp: code.replace(/\s+/g, "") },
      })
      await auth.refreshMe()
      setPhase("idle"); setCode(""); setPassword("")
      onClose()
    } catch (e) { setErr((e as Error).message) } finally { setBusy(false) }
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Two-factor authentication"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          {phase === "idle" && !me?.totp_enabled && (
            <Button onClick={startEnroll} disabled={busy}>{busy ? <Spinner /> : null}Begin setup</Button>
          )}
          {phase === "idle" && me?.totp_enabled && (
            <Button variant="destructive" onClick={() => { setErr(null); setPhase("disabling") }} disabled={busy}><ShieldOffIcon className="h-3 w-3" />Disable</Button>
          )}
          {phase === "disabling" && (
            <Button variant="destructive" onClick={disable} disabled={busy || code.replace(/\s+/g, "").length < 6 || !password}>{busy ? <Spinner /> : null}Confirm disable</Button>
          )}
          {phase === "enrolling" && (
            <Button onClick={activate} disabled={busy || code.replace(/\s+/g, "").length < 6}>{busy ? <Spinner /> : null}Confirm</Button>
          )}
        </>
      }
    >
      {phase === "idle" && me?.totp_enabled && (
        <p className="text-sm text-muted-foreground">2FA is currently enabled on your account. Disable only if you must.</p>
      )}
      {phase === "idle" && !me?.totp_enabled && (
        <p className="text-sm text-muted-foreground">
          Use an authenticator app (Authy, 1Password, Google Authenticator). Begin setup to scan a QR.
        </p>
      )}
      {phase === "enrolling" && enroll && (
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">Scan with your authenticator, then enter the 6-digit code.</p>
          <div className="flex justify-center rounded-lg bg-white p-3 w-fit mx-auto">
            <QRCodeSVG value={enroll.provisioning_uri} size={192} level="M" />
          </div>
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer">Can&rsquo;t scan? Enter the key manually</summary>
            <code className="block break-all mt-2 bg-muted/50 p-2 rounded font-mono text-[11px] select-all">{enroll.secret}</code>
          </details>
          <Input inputMode="numeric" autoComplete="one-time-code" placeholder="123 456" value={code} onChange={(e) => setCode(e.target.value)} autoFocus />
        </div>
      )}
      {phase === "disabling" && (
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">To turn off 2FA, confirm your password and a current code from your authenticator.</p>
          <Input type="password" autoComplete="current-password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
          <Input inputMode="numeric" autoComplete="one-time-code" placeholder="123 456" value={code} onChange={(e) => setCode(e.target.value)} />
        </div>
      )}
      {phase === "show-recovery" && (
        <div className="space-y-3 text-sm">
          <Toast variant="ok">2FA enabled. Save these recovery codes — they&rsquo;re shown ONCE.</Toast>
          <pre className="text-[12px] bg-muted/50 p-3 rounded font-mono">{recovery.join("\n")}</pre>
          <Button onClick={() => {
            navigator.clipboard.writeText(recovery.join("\n"))
          }}>Copy</Button>
        </div>
      )}
      {err && <Toast variant="destructive">{err}</Toast>}
    </Sheet>
  )
}


function InlineEdit({ value, placeholder, mono, onSave }: {
  value: string; placeholder?: string; mono?: boolean; onSave: (v: string) => Promise<void>
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [busy, setBusy] = useState(false)
  if (!editing) {
    return (
      <span className="inline-flex items-center gap-2">
        <span className={mono ? "font-mono text-xs" : undefined}>{value || <span className="text-muted-foreground">{placeholder}</span>}</span>
        <Button variant="ghost" size="sm" onClick={() => { setDraft(value); setEditing(true) }}>Edit</Button>
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-2">
      <Input value={draft} onChange={(e) => setDraft(e.target.value)} className={`h-8 w-48 ${mono ? "font-mono text-xs" : ""}`} autoFocus
        onKeyDown={(e) => { if (e.key === "Escape") setEditing(false) }} />
      <Button size="sm" disabled={busy} onClick={async () => {
        setBusy(true)
        try { await onSave(draft.trim()); setEditing(false) } catch { /* shown by parent */ } finally { setBusy(false) }
      }}>{busy ? <Spinner /> : null}Save</Button>
      <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
    </span>
  )
}


function describeAgent(ua: string | null): string {
  if (!ua) return "Unknown device"
  const u = ua.toLowerCase()
  const os = u.includes("iphone") ? "iPhone" : u.includes("ipad") ? "iPad" : u.includes("android") ? "Android"
    : u.includes("mac os") || u.includes("macintosh") ? "Mac" : u.includes("windows") ? "Windows" : u.includes("linux") ? "Linux" : ""
  const app = u.includes("lucymac") || u.includes("lucy/") ? "Lucy app"
    : u.includes("edg/") ? "Edge" : u.includes("chrome/") ? "Chrome" : u.includes("firefox/") ? "Firefox"
    : u.includes("safari/") ? "Safari" : "Browser"
  return [app, os].filter(Boolean).join(" · ")
}

/** Live refresh sessions: one row per browser / device, revoke individually. */
function SessionsList() {
  const qc = useQueryClient()
  const sessions = useQuery({
    queryKey: ["my-sessions"],
    queryFn: () => api<SessionOut[]>("/api/auth/sessions", {
      method: "POST", body: { refresh_token: auth._refreshToken() },
    }),
  })
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/api/auth/sessions/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-sessions"] }),
  })
  if (sessions.isLoading) return <Spinner />
  const rows = sessions.data ?? []
  if (rows.length === 0) return null
  return (
    <div className="divide-y divide-border rounded-xl border border-border">
      {rows.map((s) => (
        <div key={s.id} className="flex items-center gap-3 px-3 py-2.5">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-[13px]">
              <span className="truncate font-medium">{describeAgent(s.user_agent)}</span>
              {s.current && <Badge variant="ok">this device</Badge>}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {s.ip ? `${s.ip} · ` : ""}active {timeAgo(s.last_used_at || s.issued_at)} · since {s.issued_at.slice(0, 10)}
            </div>
          </div>
          {!s.current && (
            <Button variant="ghost" size="sm" disabled={revoke.isPending} onClick={() => revoke.mutate(s.id)}>
              Sign out
            </Button>
          )}
        </div>
      ))}
    </div>
  )
}
